import React, { useState, useEffect, useRef } from "react";
import { Link } from "gatsby";
import styled from "styled-components";
import { motion, useScroll } from "framer-motion";

const Navigation = styled.nav`
  padding: 0 30px;
  line-height: 90px;

  & ol {
    & li {
      display: block;
      &#link-exhibition {
        width: 108px;
      }
      &#link-archive {
        width: 83px;
      }
      &#link-residency {
        width: 104px;
      }
      &#link-info {
        width: 48px;
      }
      &#link-library {
        width: 78px;
      }
      &#link-news {
        width: 63px;
      }
      &::after {
        content: ", ";
      }
      &:last-of-type {
        &::after {
          content: "";
        }
      }
      text-align: center;
      & a {
        display: inline-block;
        &.current,
        &:hover {
          font-style: italic;
        }
      }
    }
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const LogoContainer = styled.div`
  display: flex;
  position: relative;
`;

const PHRASE = "Painters Painting Paintings.";
const P_INDICES = [0, 9, 18];

// Debounce utility
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

export const DesktopMenu = ({ links, location, currentExhibition }) => {
  const { scrollY } = useScroll();
  const letters = PHRASE.split("");
  const [scroll, setScroll] = useState(0);
  const letterRefs = useRef([]);
  const pppRefs = useRef([]);
  const [startPPositions, setStartPPositions] = useState([0, 0, 0]);
  const [finalPPositions, setFinalPPositions] = useState([0, 0, 0]);
  const [isMeasured, setIsMeasured] = useState(false);
  const logoContainerRef = useRef();
  const hiddenContainerRef = useRef();

  // Function to measure P positions (does not set isMeasured - that's controlled separately)
  const measurePPositions = React.useCallback((setReady = false) => {
    if (!logoContainerRef.current) return;

    const containerRect = logoContainerRef.current.getBoundingClientRect();
    if (containerRect.width === 0) return; // Don't measure if not yet visible

    const startPs = P_INDICES.map((idx) => {
      const ref = letterRefs.current[idx];
      return ref ? ref.getBoundingClientRect().left - containerRect.left : 0;
    });
    setStartPPositions(startPs);

    const finalPs = [0, 1, 2].map((idx) => {
      const ref = pppRefs.current[idx];
      return ref ? ref.getBoundingClientRect().left - containerRect.left : 0;
    });
    setFinalPPositions(finalPs);

    // Only mark as measured when explicitly told (after fonts load and layout settled)
    if (setReady) {
      setIsMeasured(true);
    }
  }, []);

  // Debounced version for resize/scroll (only updates positions, doesn't change isMeasured)
  const debouncedMeasure = React.useRef(
    debounce(() => measurePPositions(false), 100)
  ).current;

  useEffect(() => {
    // Wait for refs to be attached and layout to settle
    const measureWithDelay = (setReady = false) => {
      if (logoContainerRef.current && letterRefs.current[0] && pppRefs.current[0]) {
        // Multiple RAFs to ensure browser has painted and settled layout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => measurePPositions(setReady));
          });
        });
      }
    };

    // Initial measure after mount
    measureWithDelay(false);

    // Only set isMeasured=true after fonts are loaded to ensure accurate positioning
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        // Small extra delay after fonts ready to ensure reflow is complete
        setTimeout(() => measureWithDelay(true), 150);
      });
    } else {
      setTimeout(() => measureWithDelay(true), 300);
    }

    // Use ResizeObserver for more reliable measurement updates
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined" && logoContainerRef.current) {
      resizeObserver = new ResizeObserver(() => debouncedMeasure());
      resizeObserver.observe(logoContainerRef.current);
    }

    window.addEventListener("resize", debouncedMeasure);
    window.addEventListener("scroll", debouncedMeasure, true);

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      window.removeEventListener("resize", debouncedMeasure);
      window.removeEventListener("scroll", debouncedMeasure, true);
    };
  }, [measurePPositions, debouncedMeasure]);

  useEffect(() => {
    const unsubscribe = scrollY.onChange((latest) => {
      setScroll(latest);
    });
    return () => unsubscribe();
  }, [scrollY]);

  function getLogoOpacity() {
    if (scroll > 195) return 0;
    if (scroll > 100 && scroll <= 195) {
      return 1 - (scroll - 100) / 95;
    }
    return 1;
  }

  function getRedPLeft(i) {
    if (scroll <= 200) {
      return startPPositions[i];
    }
    if (scroll > 200 && scroll <= 300) {
      const progress = (scroll - 200) / 100;
      const start = startPPositions[i];
      const end = finalPPositions[i];
      return start + (end - start) * progress;
    }
    return finalPPositions[i];
  }

  return (
    <Navigation className="display-flex space-between">
      {/* Hidden PPP for measurement */}
      <div
        style={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          top: 0,
        }}
      >
        <div ref={hiddenContainerRef} style={{ position: "relative" }}>
          {[..."PPP"].map((letter, i) => (
            <span key={i} ref={(el) => (pppRefs.current[i] = el)}>
              {letter}
            </span>
          ))}
        </div>
      </div>
      {/* Full logo for measurement and display */}
      <StyledLink to="/">
        <LogoContainer ref={logoContainerRef} style={{ position: "relative" }}>
          <motion.div
            style={{
              display: "flex",
              alignItems: "center",
              opacity: getLogoOpacity(),
            }}
          >
            {letters.map((letter, i) => {
              const isPLetter = P_INDICES.includes(i);
              return (
                <motion.span
                  key={`logo-letter-${i}`}
                  ref={(el) => (letterRefs.current[i] = el)}
                  style={{
                    opacity: 1,
                    fontWeight: "normal",
                    marginRight: letter === " " ? 0 : 0,
                    minWidth: letter === " " ? "0.5em" : undefined,
                    // Hide inline P's once absolute P's are rendered to prevent doubling
                    visibility: isPLetter && isMeasured ? "hidden" : "visible",
                  }}
                >
                  {letter === " " ? "\u00A0" : letter}
                </motion.span>
              );
            })}
          </motion.div>
            {/* Absolute red P's at animated positions */}
            {isMeasured && startPPositions.map((_, i) => (
              <span
                key={`abs-p-${i}`}
                style={{
                  position: "absolute",
                  left: getRedPLeft(i),
                  top: 0,
                  pointerEvents: "none",
                  zIndex: 10,
                  fontSize: "inherit",
                  transition: "left 0.3s cubic-bezier(0.4,0,0.2,1)",
                }}
              >
                P
              </span>
            ))}
        </LogoContainer>
      </StyledLink>
      <ol className="display-flex">
        {currentExhibition !== null && (
          <li className="exhibition" id="link-exhibition">
            <Link to={currentExhibition} activeClassName="current">
              Exhibition
            </Link>
          </li>
        )}
        {links}
      </ol>
    </Navigation>
  );
};
